import { Platform, View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { useTranslation } from 'react-i18next';
import { staticImage } from 'shared/library/static-images';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import logo from 'shared/assets/logo.svg';
import logoDark from 'shared/assets/logo-monochrome-dark.svg';
import logoLight from 'shared/assets/logo-monochrome-light.svg';
import icon from 'shared/assets/icon.svg';
import iconLight from 'shared/assets/icon-light.svg';
import iconDark from 'shared/assets/icon-dark.svg';
import { SvgImage } from 'shared/components/svg-image';
import colors from 'shared/library/colors';
import { withNativeGradientFallback } from 'shared/library/native-gradient';
import { Button } from 'shared/components/button';
import { useRoute } from 'shared/library/routes/route';

interface AboutUsScreenProps extends Props {}

const AboutUsScreen = ({}: AboutUsScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const isWeb = Platform.OS === 'web';
  const iconContainerClassName = isWeb
    ? 'aspect-square self-center'
    : 'aspect-square flex-1 self-center';

  return (
    <>
      <View
        className={withNativeGradientFallback(
          'from-brand-green-light to-brand-cream-dark items-center gap-12 bg-gradient-to-br p-6 md:flex-row md:justify-center md:py-12',
        )}
      >
        <View className={'md:flex-1'}>
          <Text
            className={
              'text-brand-brown-dark z-10 mx-auto mt-6 max-w-2xl text-pretty text-center text-4xl font-black md:text-left md:text-6xl'
            }
          >
            {t('about_us.safe_and_trusted_pet_sales')}
          </Text>
        </View>

        <View className={'items-center md:flex-1'}>
          <View className={'mb-6 flex-row items-center justify-evenly gap-6 md:gap-12'}>
            <View className={'items-center'}>
              <Text className={'text-brand-brown-dark text-3xl font-black md:text-4xl'}>
                {'>100K'}
              </Text>
              <Text className={'text-brand-brown-dark/70'}>{t('about_us.sold_pets')}</Text>
            </View>
            <View className={'items-center'}>
              <Text className={'text-brand-brown-dark text-3xl font-black md:text-4xl'}>
                {'>2K'}
              </Text>
              <Text className={'text-brand-brown-dark/70'}>{t('about_us.breeders')}</Text>
            </View>
            <View className={'items-center'}>
              <Text className={'text-brand-brown-dark text-3xl font-black md:text-4xl'}>2019</Text>
              <Text className={'text-brand-brown-dark/70'}>{t('about_us.year_founded')}</Text>
            </View>
          </View>

          <ReviewsSummaryCard compact={true} />
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-12'}>
        <View className={`mx-auto w-full max-w-screen-xl`}>
          <View className={'gap-6 md:flex-row-reverse lg:gap-20'}>
            <View className={'md:w-1/2'}>
              <Image
                src={staticImage('dog-and-bed')}
                alt={t('about_us.image_alt')}
                containerClassName={'aspect-square w-full'}
                imageClassName={'rounded object-cover md:rounded-xl'}
                priority={true}
                sizeType={'large'}
                aspectRatio={1}
              />
            </View>

            <View className={'flex-1'}>
              <H1
                title={t('about_us.title')}
                subtitle={t('about_us.description')}
                marginTop={false}
              />

              <View className={'mt-3 flex-row flex-wrap gap-3'}>
                <Button
                  text={t('web:layout.footer.sections.dogs.buying_a_dog')}
                  href={route('buying-a-dog')}
                  color={'brown'}
                />
                <Button
                  text={t('web:layout.footer.sections.cats.buying_a_cat')}
                  href={route('buying-a-cat')}
                  color={'brown'}
                />
              </View>

              <H2
                title={t('about_us.our_story')}
                subtitle={t('about_us.our_story_description')}
                marginBottom={false}
              />

              <H2
                title={t('about_us.our_vision')}
                subtitle={t('about_us.our_vision_description')}
                marginBottom={false}
              />

              <H2
                title={t('about_us.our_logo')}
                subtitle={t('about_us.our_logo_description', {
                  green: colors.brand.green,
                  cream: colors.brand['cream-light'],
                  brown: colors.brand['brown-dark'],
                })}
              />

              <View className={'gap-3'}>
                <View className={'border-brand-brown-dark/5 border bg-white p-3 md:p-6'}>
                  <SvgImage
                    src={logo}
                    alt={'Get a Pet Logo'}
                    containerClassName={'aspect-logo w-1/2 self-center'}
                  />
                </View>

                <View className={'bg-brand-cream p-3 md:p-6'}>
                  <SvgImage
                    src={logoDark}
                    alt={'Get a Pet Logo'}
                    containerClassName={'aspect-logo w-1/2 self-center'}
                  />
                </View>

                <View className={'bg-brand-brown-dark p-3 md:p-6'}>
                  <SvgImage
                    src={logoLight}
                    alt={'Get a Pet Logo'}
                    containerClassName={'aspect-logo w-1/2 self-center'}
                  />
                </View>

                <View
                  className={
                    isWeb
                      ? 'flex-cols-3 border-brand-brown-dark/5 flex-row gap-3 border bg-white p-3 md:gap-6 md:p-6'
                      : 'border-brand-brown-dark/5 flex-row gap-3 border bg-white p-3'
                  }
                >
                  <SvgImage
                    src={icon}
                    alt={'Get a Pet Icon'}
                    containerClassName={iconContainerClassName}
                  />

                  <SvgImage
                    src={iconDark}
                    alt={'Get a Pet Icon'}
                    containerClassName={iconContainerClassName}
                  />

                  <SvgImage
                    src={iconLight}
                    alt={'Get a Pet Icon'}
                    containerClassName={iconContainerClassName}
                  />
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>
    </>
  );
};

export { AboutUsScreen };
export type { AboutUsScreenProps };
